import { CBLocalizedTextObject } from "cbcore-ts/compiledScripts/CBDataInterfaces"
import {
    IF,
    IS,
    nil,
    NO,
    UIButton,
    UICore,
    UIDialogView,
    UIFunctionExtender,
    UIImageView,
    UINativeScrollView,
    UIObject,
    UIRectangle,
    UIScrollView,
    UITextView,
    UIView,
    YES
} from "uicore-ts"
import { LanguageService } from "../../LanguageService"
import { RHDialogs } from "../RH/Utils_slop/RHDesignTokens"
import { CBColor } from "./CBColor"
import { CBDialogView } from "./CBDialogView"
import { CBTextField } from "./CBTextField"


class LoadingIndicatorDialogContentView extends UIView {

    // spinnerView is positioned via UIView's frame (an inline CSS transform).
    // Bootstrap's .spinner-border keyframes also animate transform, and the two
    // fight over the same CSS property — the spin visibly slides/snaps instead
    // of rotating in place. Isolating the Bootstrap class on this nested plain
    // element (never touched by frame-based positioning) keeps its transform
    // animation the only one in play, so it spins cleanly.
    readonly _spinnerGlyphElement = document.createElement("div")

    readonly spinnerView = new UIView()

    constructor() {
        super()

        this._spinnerGlyphElement.classList.add("spinner-border")
        // Raw DOM node outside the frame system (deliberately, see comment above) —
        // cssText here, not individual .style.width/.height writes, keeps this from
        // reading as a UIView positioning assignment.
        this._spinnerGlyphElement.style.cssText = `width: 100%; height: 100%; color: ${RHDialogs.loadingIndicatorColor.stringValue};`
        this._spinnerGlyphElement.setAttribute("role", "status")
        this._spinnerGlyphElement.setAttribute("aria-label", "Laadimine")
        this.spinnerView.viewHTMLElement.appendChild(this._spinnerGlyphElement)
        this.addSubview(this.spinnerView)
    }

    override layoutSubviews() {
        super.layoutSubviews()

        this.contentBounds
            .rectangleWithHeight(RHDialogs.loadingIndicatorSize, 0.5)
            .rectangleWithWidth(RHDialogs.loadingIndicatorSize, 0.5)
            .assignedAsFrameOfView(this.spinnerView)
    }

    override intrinsicContentHeight() {
        return RHDialogs.loadingIndicatorDialogContentHeight
    }

    override intrinsicContentWidth() {
        return RHDialogs.loadingIndicatorDialogContentWidth
    }

}


export class CBDialogViewShower<DialogViewViewType extends CBDialogView = CBDialogView> extends UIObject {
    
    dialogView: UIDialogView<DialogViewViewType>
    static currentDialogViewShower: CBDialogViewShower | undefined | null
    static nextShowDialogFunctions: Array<{
        showDialogFunction: Function
        dialogShower: CBDialogViewShower
    }> = []
    static currentActionIndicatorDialogViewShower: CBDialogViewShower = nil

    _dismissCallback: Function = nil
    _dismissDialogViewWithoutLifecycle: ((animated?: boolean) => void) | undefined
    _dismissLifecycleOverride: (() => void) | undefined
    _showDialogFunction: Function = nil
    _dismissalCompleted = NO
    
    constructor(elementID?: string, core?: UICore) {
        
        super()
        
        // @ts-ignore
        this.dialogView = new UIDialogView<CBDialogView>()
        // @ts-ignore
        this.dialogView.view = new CBDialogView(elementID)
        this.dialogView.view.backgroundColor = RHDialogs.dialogSurfaceBackgroundColor
        
        this.dialogView.core = this.dialogView.core || core
        
        const dialogLayoutFunction = this.dialogView.layoutSubviews.bind(this.dialogView)
        this.dialogView.layoutSubviews = () => {
            
            dialogLayoutFunction()
            
            let height: number
            let width: number
            
            if (this.dialogView._fillsViewport) {
                height = window.innerHeight / UIView.pageScale
                width = window.innerWidth / UIView.pageScale
            }
            else {
                const superview = this.dialogView.superview
                const superviewBounds: UIRectangle = IF(superview?.isKindOfClass(UINativeScrollView))(
                    () => superview?.scrollSize)
                    .ELSE_IF(superview?.isKindOfClass(UIScrollView))(() => superview?.scrollSize)
                    .ELSE(() => superview?.frame)
                height = superviewBounds?.height || window.innerHeight
                width = superviewBounds?.width || window.innerWidth
            }
            
            this.dialogView.view.frame = new UIRectangle(
                0,
                0,
                height,
                width
            ).rectangleWithHeight(
                this.dialogView.view.intrinsicContentHeight(this.getDialogWidth()),
                0.5
            ).rectangleWithWidth(this.getDialogWidth(), 0.5)
            
        }
        
        
        this.dialogView.view.yesButton.addTargetForControlEvents([
            UIButton.controlEvent.PointerUpInside, UIButton.controlEvent.EnterDown
        ], () => this.yesButtonWasPressed())
        
        this.dialogView.view.noButton.addTargetForControlEvents([
            UIButton.controlEvent.PointerUpInside, UIButton.controlEvent.EnterDown
        ], () => this.noButtonWasPressed())
        
    }
    
    
    // ─── Keyboard trap ────────────────────────────────────────────────────────
    
    // The buttons available for keyboard navigation in the current dialog,
    // ordered left-to-right as they appear visually. Built fresh each time
    // _attachKeyboardTrap is called.
    private _focusedButtons: UIButton[] = []
    private _focusedButtonIndex = 0
    
    private _keyboardHandler = (event: KeyboardEvent) => {

        if (this._focusedButtons.length === 0) {
            // No button to control - let the content handle its own keys, except Escape.
            if (event.key === "Escape") {
                event.stopImmediatePropagation()
                event.preventDefault()
                this.dialogView.dismiss()
            }
            return
        }

        // Swallow the event — nothing beneath this dialog should see it
        event.stopImmediatePropagation()

        switch (event.key) {
            
            case "ArrowLeft":
            case "ArrowUp":
                event.preventDefault()
                this._moveFocus(-1)
                break
            
            case "ArrowRight":
            case "ArrowDown":
                event.preventDefault()
                this._moveFocus(1)
                break
            
            case "Enter":
                event.preventDefault()
                this._activateFocusedButton()
                break
            
            case "Escape":
                event.preventDefault()
                // Escape always triggers the safe / dismiss button (index 0)
                this._focusedButtonIndex = 0
                this._activateFocusedButton()
                break
            
        }
        
    }
    
    private _moveFocus(delta: number) {
        
        if (this._focusedButtons.length === 0) {
            return
        }
        
        this._setFocusedButtonVisualState(this._focusedButtons[this._focusedButtonIndex], NO)
        this._focusedButtonIndex = (this._focusedButtonIndex + delta + this._focusedButtons.length) %
            this._focusedButtons.length
        this._setFocusedButtonVisualState(this._focusedButtons[this._focusedButtonIndex], YES)
        
    }
    
    private _setFocusedButtonVisualState(button: UIButton, focused: boolean) {
        
        if (this.dialogView.view.isMessageDialog) {
            button.focused = NO
            return
        }
        
        button.focused = focused
        
    }
    
    private _activateFocusedButton() {
        
        const button = this._focusedButtons[this._focusedButtonIndex]
        if (!IS(button)) {
            return
        }
        
        // If this button is the cancel button on a message dialog (the sole dismiss
        // button), call dialogView.dismiss() directly.  Routing through
        // sendControlEventForKey dispatches a real DOM keydown event which is
        // immediately intercepted and swallowed by our own capture-phase
        // _keyboardHandler before the framework can map it to an EnterDown control
        // event — meaning the cancel button's EnterDown targets (including the
        // this.dialogView?.dismiss() call in initCancelButtonIfNeeded) never fire,
        // so unlockScroll is never called and the page stays non-scrollable.
        if (
            button === this.dialogView.view.cancelButton ||
            button === this.dialogView.view.closeButton
        ) {
            this.dialogView.dismiss()
            return
        }
        
        button.sendControlEventForKey(UIButton.controlEvent.EnterDown, new KeyboardEvent("keydown"))
        
    }
    
    _attachKeyboardTrap(focusesDefaultButton = YES) {
        
        const view = this.dialogView.view
        
        // Build the button list: safe/dismiss button(s) first, confirm last.
        // The ordering matches the visual left-to-right layout in CBDialogView:
        //   message dialog:  [cancelButton]
        //   question dialog: [noButton, yesButton]
        this._focusedButtons = []

        if (IS(view.closeButton)) {
            this._focusedButtons.push(view.closeButton)
        }
        
        if (IS(view.noButton)) {
            this._focusedButtons.push(view.noButton)
        }
        if (IS(view.cancelButton)) {
            this._focusedButtons.push(view.cancelButton)
        }
        if (IS(view.yesButton)) {
            this._focusedButtons.push(view.yesButton)
        }
        
        // Default focus: yes button if present, otherwise the first (safe) button
        this._focusedButtonIndex = IS(view.yesButton) ? this._focusedButtons.length - 1 : 0
        if (focusesDefaultButton && this._focusedButtons.length > 0) {
            this._setFocusedButtonVisualState(this._focusedButtons[this._focusedButtonIndex], YES)
        }
        
        // Capture phase ensures this handler fires before any bubbling listener,
        // and stopImmediatePropagation inside prevents all other listeners from
        // seeing the event while the dialog is open.
        document.body.addEventListener("keydown", this._keyboardHandler, true)
        
    }
    
    private _detachKeyboardTrap() {
        
        // Clear focused state on all buttons before removing the trap
        for (const button of this._focusedButtons) {
            button.focused = NO
        }
        this._focusedButtons = []
        
        document.body.removeEventListener("keydown", this._keyboardHandler, true)
        
    }
    
    
    getDialogWidth() {
        
        const padding = this.dialogView.core.paddingLength
        
        let result = 250
        
        let titleWidth = 0
        if (IS(this.dialogView.view.titleLabel)) {
            titleWidth = this.dialogView.view.titleLabel.intrinsicContentWidth() + padding * 2
        }
        
        const contentView = this.dialogView.view.view
        if (IS(contentView)) {
            let contentInset = padding
            if (this.dialogView.view.isMessageDialog) {
                contentInset = RHDialogs.confirmationDialogContentInset
            }
            result = Math.max(
                result,
                contentView.intrinsicContentWidth(
                    contentView.viewHTMLElement.naturalHeight || 1000000000
                ) + contentInset * 2
            )
        }
        
        result = Math.max(result, titleWidth)
        if (IS(this.dialogView.view.yesButton)) {
            result = Math.max(
                result,
                RHDialogs.confirmationQuestionDialogPreferredWidth
            )
        }
        result = Math.min(result, 1000)
        
        const dialogMaxWidth = (this.dialogView.superview ||
            { "bounds": new UIRectangle(0, 0, 0, result) }).bounds.width
        
        result = Math.min(result, dialogMaxWidth)
        
        return result
        
    }
    
    
    /** Override to handle the user pressing the "Yes" / confirm button. */
    yesButtonWasPressed() {
    
    
    }
    
    /** Override to handle the user pressing the "No" / deny button. */
    noButtonWasPressed() {
    
    
    }
    
    
    /** Override to handle the user pressing the "Cancel" / close button. */
    cancelButtonWasPressed() {
    
    
    }
    
    
    /**
     * Shows a Yes / No question dialog in the given root view.
     * Wire the confirm action by overriding `yesButtonWasPressed` on the instance
     * and calling `dialogView.dismiss()` inside it.
     * The No button dismisses automatically via `CBDialogView.noButtonDismissesDialog`.
     */
    showQuestionDialogInRootView(
        questionTextObject?: CBLocalizedTextObject,
        supportingTextObject?: CBLocalizedTextObject,
        rootView = UICore.main.rootViewController.view
    ) {

        if (IS(supportingTextObject)) {
            this.dialogView.view.configureForQuestionWithSupportingText()
            this.dialogView.view.titleLabel.hidden = NO
            this.dialogView.view.titleLabel.localizedTextObject = questionTextObject
            this.dialogView.view.questionLabel.localizedTextObject = supportingTextObject
        }
        else {
            this.dialogView.view.configureForQuestionDialog()
            this.dialogView.view.titleLabel.localizedTextObject = nil
            this.dialogView.view.titleLabel.text = ""
            this.dialogView.view.titleLabel.hidden = YES
            this.dialogView.view.questionLabel.localizedTextObject = questionTextObject
        }
        
        this.dialogView.view.initYesNoButtonsIfNeeded()
        
        this.dialogView.view.noButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => this.noButtonWasPressed()
        
        this.dialogView.view.yesButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => this.yesButtonWasPressed()
        
        
        this.dialogView.showInView(rootView, YES)
        this._attachKeyboardTrap()
        
    }
    
    /**
     * Shows a Yes / No question dialog that also contains an inline text field.
     * Read the entered value via `dialogView.view.view.text` inside `yesButtonWasPressed`.
     * Same Yes/No callback semantics as `showQuestionDialogInRootView`.
     */
    showQuestionDialogWithTextFieldInRootView(
        questionTextObject?: CBLocalizedTextObject,
        supportingTextObject?: CBLocalizedTextObject,
        rootView = UICore.main.rootViewController.view
    ) {
        
        this.dialogView.view.initTitleLabelIfNeeded()
        this.dialogView.view.initQuestionLabelIfNeeded()
        
        if (IS(supportingTextObject)) {
            this.dialogView.view.titleLabel.hidden = NO
            this.dialogView.view.titleLabel.localizedTextObject = questionTextObject
            this.dialogView.view.questionLabel.localizedTextObject = supportingTextObject
        }
        else {
            this.dialogView.view.titleLabel.localizedTextObject = nil
            this.dialogView.view.titleLabel.text = ""
            this.dialogView.view.titleLabel.hidden = YES
            this.dialogView.view.questionLabel.localizedTextObject = questionTextObject
        }
        
        this.dialogView.view.view = new CBTextField()
        
        this.dialogView.view.initYesNoButtonsIfNeeded()
        
        this.dialogView.view.noButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => this.noButtonWasPressed()
        
        this.dialogView.view.yesButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => this.yesButtonWasPressed()
        
        
        this.dialogView.showInView(rootView, YES)
        // UIButton.focused calls native focus() without preventScroll. A text-entry
        // dialog must focus only its field or long pages jump behind the overlay.
        this._attachKeyboardTrap(NO)
        const textField = this.dialogView.view.view as CBTextField
        textField.textField.textElementView.viewHTMLElement.focus({
            preventScroll: YES
        })
        
    }
    
    /**
     * Shows a simple message dialog with a single dismiss ("OK") button.
     * The `cancelButtonWasPressed` override fires when the user closes it.
     * `dismissCallback` (supplied via the static factory) is called when the
     * dialog is dismissed — i.e. when the user presses OK.
     */
    showMessageDialogInRootView(
        titleTextObject?: CBLocalizedTextObject,
        rootView = UICore.main.rootViewController.view
    ) {
        
        this.dialogView.view.initTitleLabelIfNeeded()
        this.dialogView.view.titleLabel.localizedTextObject = titleTextObject
        this.dialogView.view.configureForMessageDialog()
        
        this.dialogView.view.initCancelButtonIfNeeded()
        
        // noinspection JSConstantReassignment
        this.dialogView.view.cancelButton.controlEventTargetAccumulator
            .EnterDown.PointerUpInside = () => this.cancelButtonWasPressed()
        
        this.dialogView.showInView(rootView, YES)
        this._attachKeyboardTrap()
        
    }
    
    /**
     * Shows an arbitrary `UIView` inside the standard dialog chrome.
     * An optional Cancel button is shown by default (`showCancelButton = YES`).
     * The `cancelButtonWasPressed` override and the dismiss callback both fire
     * when the user closes the dialog.
     */
    showDialogInRootView(view: UIView, showCancelButton = YES) {
        
        this.dialogView.view.view = view
        if (showCancelButton) {
            this.dialogView.view.initCancelButtonIfNeeded()
        }
        
        // noinspection JSConstantReassignment
        this.dialogView.view.cancelButton.controlEventTargetAccumulator
            .EnterDown.PointerUpInside = () => this.cancelButtonWasPressed()
        
        this.dialogView.showInRootView(YES)
        this._attachKeyboardTrap()
        
    }
    
    
    /**
     * Shows an image in a dialog.
     * When `deleteImageCallback` is provided a "Delete" (No) button appears alongside
     * a "Close" (Yes) button; pressing Delete triggers a confirmation sub-dialog before
     * calling `deleteImageCallback`. Without `deleteImageCallback` only a Close button
     * is shown.
     */
    showImageDialogInRootView(imageURL: string, deleteImageCallback?: Function) {
        
        const loadingLabel = new UITextView()
        
        loadingLabel.text = "Loading image."
        
        loadingLabel.textAlignment = UITextView.textAlignment.center
        
        this.dialogView.view.view = loadingLabel
        
        
        const imageView = new UIImageView()
        
        imageView.imageSource = imageURL
        
        imageView.viewHTMLElement.onload = () => {
            this.dialogView.view.view = imageView
            
            imageView.setNeedsLayoutUpToRootView()
        }
        
        imageView.fillMode = UIImageView.fillMode.aspectFitIfLarger
        
        
        if (IS(deleteImageCallback)) {
            
            this.dialogView.view.initYesNoButtonsIfNeeded()
            this.dialogView.view.yesButton.titleLabel.text = "Close"
            this.dialogView.view.noButton.titleLabel.text = "Delete"
            this.dialogView.view.noButtonDismissesDialog = NO
            
            this.dialogView.view.noButton.addTargetForControlEvents([
                UIButton.controlEvent.EnterDown, UIButton.controlEvent.PointerUpInside
            ], () => {
                
                const dialogShower = CBDialogViewShower._dialogShowerWithDismissCallback(() => {
                    
                    //this.dialogView.dismiss()
                    
                })
                
                const textObject = LanguageService.localizedTextObjectForText("Delete this image.")
                
                dialogShower.showQuestionDialogInRootView(textObject)
                
                dialogShower.yesButtonWasPressed = () => {
                    
                    deleteImageCallback()
                    dialogShower.dialogView.dismiss()
                    
                }
                
            })
            
            this.dialogView.view.yesButton.addTargetForControlEvents([
                UIButton.controlEvent.EnterDown, UIButton.controlEvent.PointerUpInside
            ], () => this.dialogView.dismiss())
            
        }
        else {
            
            this.dialogView.view.initCancelButtonIfNeeded()
            this.dialogView.view.cancelButton.titleLabel.text = "Close"
            
        }
        
        
        this.dialogView.showInRootView(YES)
        this._attachKeyboardTrap()
        
        
    }
    
    /**
     * Shows a non-dismissible loading spinner dialog without visible status text.
     * The message parameter remains for compatibility with existing callers.
     */
    showActionIndicatorDialogInRootView(_message: string, rootView = UICore.main.rootViewController.view) {
        
        const loadingContentView = new LoadingIndicatorDialogContentView()
        
        this.dialogView.zIndex = 150
        this.dialogView.view.view = loadingContentView
        
        this.dialogView.view.backgroundColor = CBColor.primaryContentColor.colorWithAlpha(0.5)
        
        this.dialogView.dismissesOnTapOutside = NO
        
        CBDialogViewShower.currentActionIndicatorDialogViewShower = this
        
        this.dialogView.showInView(rootView, NO)
        
    }
    
    
    /**
     * Pops and displays the next queued dialog, if any.
     * Called automatically when a dialog is dismissed — you rarely need to call this
     * directly.
     */
    static showNextDialog() {

        if (IS(CBDialogViewShower.currentDialogViewShower)) {
            return
        }
        const nextDialog = CBDialogViewShower.nextShowDialogFunctions.firstElement
        if (!nextDialog) {
            return
        }
        CBDialogViewShower.nextShowDialogFunctions.removeElementAtIndex(0)
        CBDialogViewShower.currentDialogViewShower = nextDialog.dialogShower
        nextDialog.showDialogFunction()
        
    }
    
    
    /**
     * Shows a simple message alert with a single OK button.
     * `dismissCallback` is called when the user dismisses the dialog (presses OK).
     * If another dialog is already open this one is queued and shown after the
     * current one is dismissed.
     * Returns the shower so a route or other owning lifecycle can dismiss this
     * specific alert before it becomes stale.
     *
     * @example
     * CBDialogViewShower.alert("Salvestamine õnnestus!")
     * CBDialogViewShower.alert("Viga salvestamisel", () => { ... })
     */
    static alert(text: string, dismissCallback: Function = nil, rootView = UICore.main.rootViewController.view) {
        
        const dialogShower = CBDialogViewShower._dialogShowerWithDismissCallback(dismissCallback)
        const textObject = LanguageService.localizedTextObjectForText(text)
        const showDialogFunction = dialogShower.showMessageDialogInRootView.bind(dialogShower, textObject, rootView)
        CBDialogViewShower._showDialogWithFunction(showDialogFunction, dialogShower)

        return dialogShower
        
    }
    
    
    /**
     * Same as `alert` but accepts a pre-built `CBLocalizedTextObject` instead of
     * a plain string, enabling multi-language support.
     */
    static localizedAlert(textObject: CBLocalizedTextObject, dismissCallback: Function = nil) {
        
        const dialogShower = CBDialogViewShower._dialogShowerWithDismissCallback(dismissCallback)
        const showDialogFunction = dialogShower.showMessageDialogInRootView.bind(dialogShower, textObject)
        CBDialogViewShower._showDialogWithFunction(showDialogFunction, dialogShower)
        
    }
    
    
    /**
     * Shows a Yes / No confirmation dialog.
     * `dismissCallback` fires when the dialog is dismissed for any reason —
     * No button, Escape, or tapping outside. It does NOT fire on Yes.
     * To handle Yes, override `yesButtonWasPressed` on the returned instance
     * and call `dialogView.dismiss()` inside it.
     * Returns the `CBDialogViewShower` instance for further configuration.
     *
     * @example
     * const shower = CBDialogViewShower.showQuestionDialog("Kas oled kindel?")
     * shower.yesButtonWasPressed = () => {
     *     shower.dialogView.dismiss()
     *     this.deleteItem()
     * }
     */
    static showQuestionDialog(questionText: string, dismissCallback: Function = nil) {
        
        const dialogShower = CBDialogViewShower._dialogShowerWithDismissCallback(dismissCallback)
        const textObject = LanguageService.localizedTextObjectForText(questionText)
        const showDialogFunction = dialogShower.showQuestionDialogInRootView.bind(dialogShower, textObject)
        CBDialogViewShower._showDialogWithFunction(showDialogFunction, dialogShower)
        
        return dialogShower
        
    }


    /**
     * Shows the standard Yes / No question dialog with a separate close action.
     * Closing through the top-right button, Escape, or the backdrop only dismisses
     * the dialog and invokes `dismissCallback`; it does not choose Yes or No.
     */
    static showQuestionDialogWithCloseButton(
        questionText: string,
        dismissCallback: Function = nil
    ) {

        const dialogShower = CBDialogViewShower._dialogShowerWithDismissCallback(dismissCallback)
        dialogShower.dialogView.view.initCloseButtonIfNeeded()
        const textObject = LanguageService.localizedTextObjectForText(questionText)
        const showDialogFunction = dialogShower.showQuestionDialogInRootView.bind(
            dialogShower,
            textObject
        )
        CBDialogViewShower._showDialogWithFunction(showDialogFunction, dialogShower)

        return dialogShower

    }


    /**
     * Shows a question dialog with a bold primary line and regular supporting
     * text beneath it. Button presentation and interaction remain unchanged.
     */
    static showQuestionDialogWithSupportingText(
        questionText: string,
        supportingText: string,
        dismissCallback: Function = nil
    ) {

        const dialogShower = CBDialogViewShower._dialogShowerWithDismissCallback(dismissCallback)
        const questionTextObject = LanguageService.localizedTextObjectForText(questionText)
        const supportingTextObject = LanguageService.localizedTextObjectForText(supportingText)
        const showDialogFunction = dialogShower.showQuestionDialogInRootView.bind(
            dialogShower,
            questionTextObject,
            supportingTextObject
        )
        CBDialogViewShower._showDialogWithFunction(showDialogFunction, dialogShower)

        return dialogShower

    }


    /**
     * Replaces the currently visible dialog with a confirmation dialog.
     *
     * Dismissing the confirmation through No, Escape, or the backdrop restores
     * the exact same dialog and content view. Confirming permanently dismisses
     * the replaced dialog after the action succeeds and advances the queue once.
     * If the action rejects, the original dialog is restored for correction.
     * Existing queued dialogs are never removed or rendered underneath either
     * side of the replacement.
     */
    static replaceDialogWithQuestion(
        dialogShowerToReplace: CBDialogViewShower,
        questionText: string,
        confirmationWasAccepted: () => Promise<void>
    ) {
        if (CBDialogViewShower.currentDialogViewShower !== dialogShowerToReplace) {
            throw new Error("Only the currently visible dialog can be replaced.")
        }
        if (
            !dialogShowerToReplace._dismissDialogViewWithoutLifecycle ||
            !dialogShowerToReplace._showDialogFunction
        ) {
            throw new Error("The dialog cannot be restored after replacement.")
        }

        dialogShowerToReplace._detachKeyboardTrap()
        dialogShowerToReplace._dismissDialogViewWithoutLifecycle(NO)

        const confirmationShower = CBDialogViewShower._dialogShowerWithDismissCallback(nil)
        const textObject = LanguageService.localizedTextObjectForText(questionText)
        const showConfirmation = confirmationShower.showQuestionDialogInRootView.bind(
            confirmationShower,
            textObject
        )
        confirmationShower._showDialogFunction = showConfirmation

        let confirmationIsInFlight = NO
        let replacementWasAccepted = NO

        confirmationShower._dismissLifecycleOverride = () => {
            if (replacementWasAccepted) {
                CBDialogViewShower.currentDialogViewShower = dialogShowerToReplace
                dialogShowerToReplace._completeDismissalLifecycle()
                return
            }

            CBDialogViewShower.currentDialogViewShower = dialogShowerToReplace
            dialogShowerToReplace._showDialogFunction()
        }

        CBDialogViewShower.currentDialogViewShower = confirmationShower
        showConfirmation()

        confirmationShower.yesButtonWasPressed = async () => {
            if (confirmationIsInFlight) {
                return
            }
            confirmationIsInFlight = YES
            confirmationShower.dialogView.view.yesButton.enabled = NO
            confirmationShower.dialogView.view.noButton.enabled = NO
            confirmationShower.dialogView.dismissesOnTapOutside = NO
            confirmationShower._detachKeyboardTrap()

            try {
                await confirmationWasAccepted()
                replacementWasAccepted = YES
            }
            catch (error) {
                console.error("Error confirming dialog action:", error)
            }
            finally {
                confirmationShower.dialogView.dismiss(NO)
            }
        }

        return confirmationShower
    }
    
    /**
     * Shows a Yes / No confirmation dialog that also contains a text field.
     * `dismissCallback` fires when the dialog is dismissed for any reason —
     * No button, Escape, or tapping outside. It does NOT fire on Yes.
     * To handle Yes, override `yesButtonWasPressed` on the returned instance,
     * read the value from `dialogShower.dialogView.view.view.text`, then call
     * `dialogView.dismiss()`.
     *
     * @example
     * const shower = CBDialogViewShower.showQuestionDialogWithTextField("Sisesta uus nimi:")
     * shower.yesButtonWasPressed = () => {
     *     const name = shower.dialogView.view.view.text
     *     shower.dialogView.dismiss()
     *     this.rename(name)
     * }
     */
    static showQuestionDialogWithTextField(
        questionText: string,
        dismissCallback: Function = nil,
        rootView = UICore.main.rootViewController.view
    ) {
        
        const dialogShower = CBDialogViewShower._dialogShowerWithDismissCallback(dismissCallback) as CBDialogViewShower<CBDialogView<CBTextField>>
        const textObject = LanguageService.localizedTextObjectForText(questionText)
        const showDialogFunction = () => dialogShower.showQuestionDialogWithTextFieldInRootView(
            textObject,
            undefined,
            rootView
        )
        CBDialogViewShower._showDialogWithFunction(showDialogFunction, dialogShower)
        
        return dialogShower
        
    }
    
    
    /**
     * Shows an image in a dialog.
     * When `deleteImageCallback` is provided a Delete button appears; pressing it
     * triggers a confirmation sub-dialog before calling `deleteImageCallback`.
     * `dismissCallback` fires when the dialog is closed for any reason.
     */
    static showImageDialog(imageURL: string, deleteImageCallback: Function = nil, dismissCallback: Function = nil) {
        
        const dialogShower = CBDialogViewShower._dialogShowerWithDismissCallback(dismissCallback)
        
        const showDialogFunction = dialogShower.showImageDialogInRootView.bind(
            dialogShower,
            imageURL,
            deleteImageCallback
        )
        CBDialogViewShower._showDialogWithFunction(showDialogFunction, dialogShower)
        
        return dialogShower
        
        
    }
    
    /**
     * Shows any arbitrary `UIView` inside the standard dialog chrome with an
     * optional Cancel button.
     * `dismissCallback` fires when the dialog is closed for any reason.
     * Returns the `CBDialogViewShower` instance for further configuration.
     *
     * @example
     * CBDialogViewShower.showDialog(myCustomView, () => {
     *     // dialog was closed
     * })
     */
    static showDialog(view: UIView, dismissCallback: Function = nil, showCancelButton = YES) {
        
        const dialogShower = CBDialogViewShower._dialogShowerWithDismissCallback(dismissCallback)
        const showDialogFunction = dialogShower.showDialogInRootView.bind(dialogShower, view, showCancelButton)
        CBDialogViewShower._showDialogWithFunction(showDialogFunction, dialogShower)
        
        return dialogShower
        
    }

    /**
     * Shows structured informational content with the standard single OK action.
     * Uses the same symmetric content inset and typography rules as text alerts,
     * while `showDialog` remains available for interactive content dialogs.
     */
    static showContentAlert(view: UIView, dismissCallback: Function = nil) {

        const dialogShower = CBDialogViewShower._dialogShowerWithDismissCallback(dismissCallback)
        const showDialogFunction = () => {
            dialogShower.dialogView.view.isMessageDialog = YES
            dialogShower.showDialogInRootView(view)
        }
        CBDialogViewShower._showDialogWithFunction(showDialogFunction, dialogShower)

        return dialogShower

    }
    
    /**
     * Shows a non-dismissible loading spinner dialog without visible status text.
     * If a spinner is already visible the existing dialog remains in place.
     * Dismiss programmatically with `CBDialogViewShower.hideActionIndicatorDialog()`.
     * `dismissCallback` fires when the dialog is eventually hidden.
     *
     * @example
     * CBDialogViewShower.showActionIndicatorDialog("Salvestamine...")
     * await doWork()
     * CBDialogViewShower.hideActionIndicatorDialog()
     */
    static showActionIndicatorDialog(message: string, dismissCallback: () => void = nil, rootView?: UIView) {
        
        if (IS(CBDialogViewShower.currentActionIndicatorDialogViewShower)) {
            return
        }
        
        const dialogShower = CBDialogViewShower._dialogShowerWithDismissCallback(
            UIFunctionExtender.functionByExtendingFunction(
                () => {
                    if (CBDialogViewShower.currentActionIndicatorDialogViewShower == dialogShower) {
                        CBDialogViewShower.currentActionIndicatorDialogViewShower = nil
                    }
                },
                dismissCallback
            )
        )
        
        dialogShower.showActionIndicatorDialogInRootView(message, rootView)
        return dialogShower
        
    }
    
    
    /**
     * Dismisses the currently visible action indicator (loading spinner) dialog.
     * Safe to call even when no spinner is shown — it will simply do nothing meaningful.
     */
    static hideActionIndicatorDialog() {
        
        CBDialogViewShower.currentActionIndicatorDialogViewShower.dialogView.dismiss()
        
        CBDialogViewShower.currentActionIndicatorDialogViewShower = nil
        
    }
    
    
    private static _dialogShowerWithDismissCallback(dismissCallback: Function) {
        
        const dialogShower = new CBDialogViewShower()
        const dismissFunction = dialogShower.dialogView.dismiss.bind(dialogShower.dialogView)
        dialogShower._dismissCallback = dismissCallback
        dialogShower._dismissDialogViewWithoutLifecycle = dismissFunction
        
        dialogShower.dialogView.dismiss = function (animated?: boolean) {
            if (!dialogShower.dialogView.isVisible) {
                return
            }
            dialogShower._detachKeyboardTrap()
            const lifecycleOverride = dialogShower._dismissLifecycleOverride
            if (lifecycleOverride) {
                dialogShower._dismissLifecycleOverride = undefined
                dismissFunction(NO)
                lifecycleOverride()
                return
            }
            dismissFunction(animated)
            dialogShower._completeDismissalLifecycle()
        }
        
        return dialogShower
        
    }


    /**
     * Dismisses this dialog whether it is currently visible or still waiting
     * in the shared dialog queue. The normal dismissal callback and queue
     * lifecycle run exactly once in either case.
     */
    dismiss() {
        if (this.dialogView.isVisible) {
            this.dialogView.dismiss()
            return
        }

        const queuedDialogIndex = CBDialogViewShower.nextShowDialogFunctions.findIndex(
            queuedDialog => queuedDialog.dialogShower === this
        )
        if (queuedDialogIndex === -1) {
            return
        }

        CBDialogViewShower.nextShowDialogFunctions.splice(queuedDialogIndex, 1)
        this._completeDismissalLifecycle()
    }


    _completeDismissalLifecycle() {
        if (this._dismissalCompleted) {
            return
        }
        this._dismissalCompleted = YES
        this._dismissCallback()
        if (CBDialogViewShower.currentDialogViewShower === this) {
            CBDialogViewShower.currentDialogViewShower = null
        }
        CBDialogViewShower.showNextDialog()
    }
    
    
    private static _showDialogWithFunction(showDialogFunction: Function, dialogShower: CBDialogViewShower) {

        dialogShower._showDialogFunction = showDialogFunction
        
        if (IS(CBDialogViewShower.currentDialogViewShower)) {
            
            CBDialogViewShower.nextShowDialogFunctions.push({
                showDialogFunction,
                dialogShower
            })
            
        }
        else {
            
            CBDialogViewShower.currentDialogViewShower = dialogShower
            showDialogFunction()
            
        }
        
    }
    
    
}
